import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SpectatorView = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [codeSubmitted, setCodeSubmitted] = useState(!!searchParams.get('code'));

  useEffect(() => {
    if (!codeSubmitted || !code) return;

    // Initial full fetch so we have names, photos, and structure
    fetchElection();

    // Open SSE stream for live vote count updates
    const streamUrl = `${API_URL}/elections/${id}/stream?code=${code}`;
    const es = new EventSource(streamUrl);

    es.onmessage = (event) => {
      try {
        const { type, counts } = JSON.parse(event.data);
        if ((type === 'init' || type === 'update') && counts) {
          // Merge incoming vote counts into existing election state
          setElection(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              positions: prev.positions.map(pos => ({
                ...pos,
                candidates: pos.candidates?.map(c => ({
                  ...c,
                  voteCount: counts[c.id] !== undefined ? counts[c.id] : (c.voteCount || 0),
                })),
              })),
            };
          });
        }
      } catch (err) {
        console.error('SSE parse error:', err);
      }
    };

    es.onerror = () => {
      // EventSource auto-reconnects on error — no action needed
      console.warn('SSE connection lost, browser will reconnect automatically');
    };

    return () => es.close();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, codeSubmitted, code]);

  const fetchElection = async () => {
    try {
      const response = await axios.get(`${API_URL}/elections/${id}/spectator?code=${code}`);
      setElection(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch election:', err);
      setError(err.response?.data?.error || 'Failed to load election');
      setElection(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (code.trim()) {
      setCodeSubmitted(true);
      setLoading(true);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getMaxVotes = (position) => {
    if (!position.candidates || position.candidates.length === 0) return 1;
    return Math.max(...position.candidates.map(c => c.voteCount || 0), 1);
  };

  // Code entry screen
  if (!codeSubmitted) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ width: '100%', maxWidth: '440px', padding: '0 1rem' }}>
          <div className="text-center mb-4">
            <Logo color="white" width="250px" />
          </div>
          <div className="card shadow-lg" style={{ borderRadius: '16px', border: 'none' }}>
            <div className="card-body p-4 p-md-5">
              <div className="text-center mb-4">
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#f3e8ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                  <i className="bi bi-eye" style={{ color: '#9333ea', fontSize: '1.5rem' }}></i>
                </div>
                <h5 style={{ fontWeight: '700', color: '#2c3e50' }}>Spectator Access</h5>
                <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                  Enter the spectator code to view live election results
                </p>
              </div>
              <form onSubmit={handleCodeSubmit}>
                <div className="mb-4">
                  <input
                    type="text"
                    className="form-control form-control-lg text-center"
                    placeholder="e.g. A1B2C3"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    style={{ fontSize: '1.25rem', fontWeight: '600', letterSpacing: '4px', borderRadius: '12px' }}
                    maxLength={8}
                    autoFocus
                  />
                </div>
                <button type="submit" className="btn w-100" disabled={!code.trim()}
                  style={{ background: 'linear-gradient(135deg, #9333ea 0%, #7c3aed 100%)', color: 'white',
                    fontWeight: '600', padding: '0.75rem', borderRadius: '12px', border: 'none' }}>
                  <i className="bi bi-eye me-2"></i>View Results
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="spinner-border text-white" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center text-white">
          <i className="bi bi-shield-lock" style={{ fontSize: '4rem' }}></i>
          <h3 className="mt-3">{error}</h3>
          <button className="btn btn-light mt-3" onClick={() => { setCodeSubmitted(false); setCode(''); setError(null); }}>
            Try Another Code
          </button>
        </div>
      </div>
    );
  }

  if (!election) return null;

  const now = new Date();
  const hasEnded = now > new Date(election.endDate);
  const hasStarted = now >= new Date(election.startDate);
  const isLive = hasStarted && !hasEnded && election.status === 'active';

  return (
    <div className="min-vh-100" style={{ background: '#f8f9fa' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '1.5rem 0' }}>
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              {election.organization?.logoUrl && (
                <img src={election.organization.logoUrl} alt="" style={{ height: '40px', marginRight: '1rem', borderRadius: '8px' }} />
              )}
              <div className="text-white">
                <h4 className="mb-0" style={{ fontWeight: '700' }}>{election.name}</h4>
                {election.organization?.name && (
                  <small style={{ opacity: 0.8 }}>{election.organization.name}</small>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              {isLive && (
                <span className="badge bg-danger" style={{ fontSize: '0.8rem', animation: 'pulse 2s infinite' }}>
                  <i className="bi bi-broadcast me-1"></i> LIVE
                </span>
              )}
              <span className="badge bg-light text-dark" style={{ fontSize: '0.75rem' }}>
                <i className="bi bi-eye me-1"></i> Spectator
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Election Info */}
      <div className="container py-4">
        <div className="row mb-4">
          <div className="col-md-6">
            <small className="text-muted">Start: {formatDate(election.startDate)}</small>
          </div>
          <div className="col-md-6 text-md-end">
            <small className="text-muted">End: {formatDate(election.endDate)}</small>
          </div>
        </div>

        {/* Positions + Results */}
        {election.positions && election.positions.length > 0 ? (
          election.positions.map(position => (
            <div key={position.id} className="card mb-4 shadow-sm" style={{ borderRadius: '12px', border: 'none' }}>
              <div className="card-header bg-white" style={{ borderBottom: '2px solid #667eea', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                <h5 className="mb-0" style={{ fontWeight: '600' }}>{position.name}</h5>
                {position.description && <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>{position.description}</p>}
              </div>
              <div className="card-body">
                {position.candidates && position.candidates.length > 0 ? (
                  position.candidates
                    .sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0))
                    .map((candidate, idx) => {
                      const maxVotes = getMaxVotes(position);
                      const pct = maxVotes > 0 ? ((candidate.voteCount || 0) / maxVotes * 100) : 0;
                      const isLeading = idx === 0 && (candidate.voteCount || 0) > 0;
                      return (
                        <div key={candidate.id} className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div className="d-flex align-items-center">
                              {candidate.photoUrl ? (
                                <img src={candidate.photoUrl} alt="" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', marginRight: '0.75rem', border: isLeading ? '2px solid #f59e0b' : '2px solid #e5e7eb' }} />
                              ) : (
                                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '700', fontSize: '1rem', marginRight: '0.75rem',
                                  border: isLeading ? '2px solid #f59e0b' : '2px solid transparent' }}>
                                  {candidate.user?.fullName?.charAt(0) || '?'}
                                </div>
                              )}
                              <div>
                                <span style={{ fontWeight: isLeading ? '700' : '500', color: isLeading ? '#2c3e50' : '#6b7280', fontSize: '1rem' }}>
                                  {candidate.user?.fullName || 'Unknown'}
                                  {isLeading && <i className="bi bi-trophy-fill ms-2" style={{ color: '#f59e0b', fontSize: '0.85rem' }}></i>}
                                </span>
                                {candidate.bio && (
                                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '1px' }}>
                                    {candidate.bio.length > 60 ? candidate.bio.substring(0, 60) + '...' : candidate.bio}
                                  </div>
                                )}
                              </div>
                            </div>
                            <span style={{ fontWeight: '700', fontSize: '1.1rem', color: '#2c3e50' }}>
                              {candidate.voteCount || 0}
                            </span>
                          </div>
                          <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                            <div className="progress-bar" style={{
                              width: `${pct}%`,
                              background: isLeading ? 'linear-gradient(90deg, #667eea, #764ba2)' : '#d1d5db',
                              borderRadius: '4px', transition: 'width 0.5s ease',
                            }}></div>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <p className="text-muted text-center py-3">No candidates yet</p>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-trophy" style={{ fontSize: '3rem' }}></i>
            <p className="mt-3">No positions available</p>
          </div>
        )}

        <p className="text-center text-muted mt-4" style={{ fontSize: '0.8rem' }}>
          {hasEnded ? 'Voting has ended — final results' : !hasStarted ? 'Voting has not started yet' : ''}
          {(hasEnded || !hasStarted) && <br />}
          Powered by VoteWave
        </p>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default SpectatorView;
